import type {
  ApprovalStatus,
  ArSummary,
  Invoice,
  InvoiceItemTemplate,
  InvoiceLineItem,
  InvoicePayment,
  InvoiceStatus,
  InvoiceTerms,
  Payment,
  PaymentSource,
  PaymentType,
} from "@/features/invoices/types";
import { apiFetch, apiFetchBlob } from "@/lib/api";

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

// --- read shapes (snake_case) -----------------------------------------------

interface ApiLineItem {
  id: string;
  invoice: string;
  description: string;
  quantity: number;
  unit_price_cents: number;
  extended_amt_cents: number;
}

interface ApiInvoice {
  id: string;
  client: string;
  client_name?: string;
  invoice_number: string;
  invoice_date: string;
  status: InvoiceStatus;
  tax_percent: number;
  tax_amt_cents: number;
  subtotal_cents?: number;
  total_cents?: number;
  applied_cents?: number;
  balance_cents?: number;
  aging_days?: number;
  terms: InvoiceTerms;
  due_date: string;
  bill_to: string;
  note_to_client: string;
  line_items: ApiLineItem[];
  created: string;
  kind?: "CAP" | "BP";
  approval_status?: ApprovalStatus;
  approval_note?: string;
  is_proforma?: boolean;
  referral_name?: string;
  referral_commission_percent?: string | null;
  referral_commission_cents?: number;
}

interface ApiAllocation {
  id: string;
  invoice: string;
  payment: string;
  amount_cents: number;
}

interface ApiPayment {
  id: string;
  client: string;
  payment_type: PaymentType;
  refund_of: string | null;
  payment_date: string;
  amount_cents: number;
  reference: string;
  source: PaymentSource;
  note: string;
  allocations: ApiAllocation[];
  refunded_cents: number;
  refundable_cents: number;
}

function toInvoice(a: ApiInvoice): Invoice {
  return {
    id: a.id,
    clientId: a.client,
    clientName: a.client_name,
    invoiceNumber: a.invoice_number,
    invoiceDate: a.invoice_date,
    status: a.status,
    taxPercent: a.tax_percent,
    taxAmtCents: a.tax_amt_cents,
    terms: a.terms,
    dueDate: a.due_date,
    billTo: a.bill_to,
    noteToClient: a.note_to_client ?? "",
    created: a.created,
    kind: a.kind ?? "CAP",
    approvalStatus: a.approval_status ?? "NOT_SUBMITTED",
    approvalNote: a.approval_note ?? "",
    isProforma: a.is_proforma ?? false,
    referralName: a.referral_name ?? "",
    referralCommissionPercent: a.referral_commission_percent ?? null,
    referralCommissionCents: a.referral_commission_cents ?? 0,
  };
}

function toLineItem(a: ApiLineItem): InvoiceLineItem {
  return {
    id: a.id,
    invoiceId: a.invoice,
    description: a.description,
    quantity: a.quantity,
    unitPriceCents: a.unit_price_cents,
    extendedAmtCents: a.extended_amt_cents,
  };
}

function toPayment(a: ApiPayment): Payment {
  return {
    id: a.id,
    clientId: a.client,
    paymentType: a.payment_type,
    refundOf: a.refund_of || undefined,
    paymentDate: a.payment_date,
    paymentAmtCents: a.amount_cents,
    reference: a.reference || undefined,
    source: a.source,
    note: a.note || undefined,
    refundedCents: a.refunded_cents,
    refundableCents: a.refundable_cents,
  };
}

function toAllocation(a: ApiAllocation): InvoicePayment {
  return {
    id: a.id,
    invoiceId: a.invoice,
    paymentId: a.payment,
    amountCents: a.amount_cents,
  };
}

export interface ClientBilling {
  invoices: Invoice[];
  lineItems: InvoiceLineItem[];
  payments: Payment[];
  invoicePayments: InvoicePayment[];
}

/** Fetch all billing data for one client, flattened into the four arrays the
 * store/selectors already operate on. */
export async function fetchClientBilling(clientId: string): Promise<ClientBilling> {
  const [inv, pay] = await Promise.all([
    apiFetch<Envelope<ApiInvoice[]>>(`/billing/invoices/?page_size=200&client=${clientId}`),
    apiFetch<Envelope<ApiPayment[]>>(`/billing/payments/?page_size=200&client=${clientId}`),
  ]);
  return {
    invoices: inv.data.map(toInvoice),
    lineItems: inv.data.flatMap((i) => i.line_items.map(toLineItem)),
    payments: pay.data.map(toPayment),
    invoicePayments: pay.data.flatMap((p) => p.allocations.map(toAllocation)),
  };
}

// --- invoice mutations ------------------------------------------------------

export async function createDraftInvoice(
  clientId: string,
  defaults: { billTo: string; taxPercent: number; terms: InvoiceTerms },
): Promise<Invoice> {
  const body = await apiFetch<Envelope<ApiInvoice>>("/billing/invoices/", {
    method: "POST",
    json: {
      client: clientId,
      bill_to: defaults.billTo,
      tax_percent: defaults.taxPercent,
      terms: defaults.terms,
    },
  });
  return toInvoice(body.data);
}

export interface InvoiceHeaderPatch {
  invoiceDate?: string;
  terms?: InvoiceTerms;
  billTo?: string;
  taxPercent?: number;
  noteToClient?: string;
}

export function updateInvoiceHeader(id: string, patch: InvoiceHeaderPatch): Promise<void> {
  const json: Record<string, unknown> = {};
  if (patch.invoiceDate !== undefined) json.invoice_date = patch.invoiceDate;
  if (patch.terms !== undefined) json.terms = patch.terms;
  if (patch.billTo !== undefined) json.bill_to = patch.billTo;
  if (patch.taxPercent !== undefined) json.tax_percent = patch.taxPercent;
  if (patch.noteToClient !== undefined) json.note_to_client = patch.noteToClient;
  return apiFetch<void>(`/billing/invoices/${id}/`, { method: "PATCH", json });
}

export interface ConfirmOptions {
  applyCredit?: boolean;
  invoiceNumber?: string;
}

export function confirmInvoice(id: string, options: ConfirmOptions = {}): Promise<unknown> {
  const json: Record<string, unknown> = {};
  if (options.applyCredit) json.apply_credit = true;
  if (options.invoiceNumber) json.invoice_number = options.invoiceNumber;
  return apiFetch(`/billing/invoices/${id}/confirm/`, { method: "POST", json });
}

export function voidInvoice(id: string): Promise<unknown> {
  return apiFetch(`/billing/invoices/${id}/void/`, { method: "POST" });
}

/** Bulk-raise a draft invoice for every BP client without one (CD/SA). Returns
 * how many were created. */
export async function bulkGenerateBpInvoices(): Promise<number> {
  const body = await apiFetch<Envelope<{ created: number }>>(
    `/billing/invoices/bulk_generate/`,
    { method: "POST", json: {} },
  );
  return body.data.created;
}

/** CD submits a draft for Super-Admin approval. */
export function submitInvoiceForApproval(id: string): Promise<unknown> {
  return apiFetch(`/billing/invoices/${id}/submit/`, { method: "POST" });
}

/** Super Admin approves a submitted invoice (clears it to be sent). */
export function approveInvoice(id: string): Promise<unknown> {
  return apiFetch(`/billing/invoices/${id}/approve/`, { method: "POST" });
}

/** Super Admin rejects a submitted invoice back to the CD with a reason. */
export function rejectInvoice(id: string, note: string): Promise<unknown> {
  return apiFetch(`/billing/invoices/${id}/reject/`, { method: "POST", json: { note } });
}

export function deleteInvoice(id: string): Promise<void> {
  return apiFetch<void>(`/billing/invoices/${id}/`, { method: "DELETE" });
}

// --- line-item mutations (nested under an invoice) --------------------------

export async function addLineItem(
  invoiceId: string,
  item: { description: string; quantity: number; unitPriceCents: number },
): Promise<InvoiceLineItem> {
  const body = await apiFetch<Envelope<ApiLineItem>>(
    `/billing/invoices/${invoiceId}/line-items/`,
    {
      method: "POST",
      json: {
        description: item.description,
        quantity: item.quantity,
        unit_price_cents: item.unitPriceCents,
      },
    },
  );
  return toLineItem(body.data);
}

export interface LineItemPatch {
  description?: string;
  quantity?: number;
  unitPriceCents?: number;
}

export function updateLineItem(
  invoiceId: string,
  itemId: string,
  patch: LineItemPatch,
): Promise<void> {
  const json: Record<string, unknown> = {};
  if (patch.description !== undefined) json.description = patch.description;
  if (patch.quantity !== undefined) json.quantity = patch.quantity;
  if (patch.unitPriceCents !== undefined) json.unit_price_cents = patch.unitPriceCents;
  return apiFetch<void>(`/billing/invoices/${invoiceId}/line-items/${itemId}/`, {
    method: "PATCH",
    json,
  });
}

export function deleteLineItem(invoiceId: string, itemId: string): Promise<void> {
  return apiFetch<void>(`/billing/invoices/${invoiceId}/line-items/${itemId}/`, {
    method: "DELETE",
  });
}

// --- payment mutations ------------------------------------------------------

interface PaymentFields {
  paymentDate: string;
  paymentAmtCents: number;
  reference?: string;
  source: PaymentSource;
  note?: string;
  /** PAYMENT (default) or CREDIT_MEMO — refunds go through `refundPayment`. */
  paymentType?: Exclude<PaymentType, "REFUND">;
}

function paymentJson(
  clientId: string | null,
  fields: PaymentFields,
  distribution: { invoiceId: string; applyCents: number }[],
) {
  const json: Record<string, unknown> = {
    payment_date: fields.paymentDate,
    amount_cents: fields.paymentAmtCents,
    reference: fields.reference ?? "",
    source: fields.source,
    note: fields.note ?? "",
    allocations: distribution
      .filter((d) => d.applyCents > 0)
      .map((d) => ({ invoice: d.invoiceId, amount_cents: d.applyCents })),
  };
  if (fields.paymentType) json.payment_type = fields.paymentType;
  if (clientId) json.client = clientId;
  return json;
}

export function recordPayment(
  clientId: string,
  fields: PaymentFields,
  distribution: { invoiceId: string; applyCents: number }[],
): Promise<unknown> {
  return apiFetch("/billing/payments/", {
    method: "POST",
    json: paymentJson(clientId, fields, distribution),
  });
}

export function updatePayment(
  paymentId: string,
  fields: PaymentFields,
  distribution: { invoiceId: string; applyCents: number }[],
): Promise<unknown> {
  return apiFetch(`/billing/payments/${paymentId}/`, {
    method: "PATCH",
    json: paymentJson(null, fields, distribution),
  });
}

export function deletePayment(paymentId: string): Promise<void> {
  return apiFetch<void>(`/billing/payments/${paymentId}/`, { method: "DELETE" });
}

export interface RefundFields {
  amountCents: number;
  source: "CHECK" | "CASH";
  reference?: string;
  reason?: string;
}

export function refundPayment(paymentId: string, fields: RefundFields): Promise<unknown> {
  return apiFetch(`/billing/payments/${paymentId}/refund/`, {
    method: "POST",
    json: {
      amount_cents: fields.amountCents,
      source: fields.source,
      reference: fields.reference ?? "",
      reason: fields.reason ?? "",
    },
  });
}

// --- invoice-item templates -------------------------------------------------

interface ApiItemTemplate {
  id: string;
  name: string;
  body: string;
  amount_cents: number;
  modified_by_name?: string;
}

function toItemTemplate(a: ApiItemTemplate): InvoiceItemTemplate {
  return {
    id: a.id,
    name: a.name,
    body: a.body,
    amountCents: a.amount_cents,
    modifiedByName: a.modified_by_name || undefined,
  };
}

export async function listItemTemplates(): Promise<InvoiceItemTemplate[]> {
  const body = await apiFetch<Envelope<ApiItemTemplate[]>>(
    "/billing/item-templates/?page_size=200",
  );
  return body.data.map(toItemTemplate);
}

export interface ItemTemplateFields {
  name: string;
  body: string;
  amountCents: number;
}

function itemTemplateJson(fields: ItemTemplateFields) {
  return { name: fields.name, body: fields.body, amount_cents: fields.amountCents };
}

export async function createItemTemplate(
  fields: ItemTemplateFields,
): Promise<InvoiceItemTemplate> {
  const body = await apiFetch<Envelope<ApiItemTemplate>>("/billing/item-templates/", {
    method: "POST",
    json: itemTemplateJson(fields),
  });
  return toItemTemplate(body.data);
}

export async function updateItemTemplate(
  id: string,
  fields: ItemTemplateFields,
): Promise<InvoiceItemTemplate> {
  const body = await apiFetch<Envelope<ApiItemTemplate>>(`/billing/item-templates/${id}/`, {
    method: "PATCH",
    json: itemTemplateJson(fields),
  });
  return toItemTemplate(body.data);
}

export function deleteItemTemplate(id: string): Promise<void> {
  return apiFetch<void>(`/billing/item-templates/${id}/`, { method: "DELETE" });
}

// --- firm-wide invoices + AR ------------------------------------------------

export interface FirmInvoiceRow {
  invoice: Invoice;
  clientName: string;
  subtotalCents: number;
  totalCents: number;
  appliedCents: number;
  balanceCents: number;
  agingDays: number;
}

export async function fetchFirmInvoices(): Promise<FirmInvoiceRow[]> {
  const body = await apiFetch<Envelope<ApiInvoice[]>>("/billing/invoices/?page_size=500");
  return body.data.map((a) => ({
    invoice: toInvoice(a),
    clientName: a.client_name ?? "",
    subtotalCents: a.subtotal_cents ?? 0,
    totalCents: a.total_cents ?? 0,
    appliedCents: a.applied_cents ?? 0,
    balanceCents: a.balance_cents ?? 0,
    agingDays: a.aging_days ?? 0,
  }));
}

interface ApiArSummary {
  current: number;
  d1_30: number;
  d31_60: number;
  d61_90: number;
  d90_plus: number;
  total: number;
}

export async function fetchArSummary(overdueOnly = false, firm?: string): Promise<ArSummary> {
  const q = new URLSearchParams();
  if (overdueOnly) q.set("overdue", "true");
  if (firm) q.set("firm", firm);
  const qs = q.toString() ? `?${q.toString()}` : "";
  const body = await apiFetch<Envelope<ApiArSummary>>(`/billing/invoices/ar-summary/${qs}`);
  // Intentionally passed through without a snake→camel map: the aging-bucket keys
  // (d1_30, d31_60, …) have no clean camelCase form, so ArSummary keeps them as-is.
  return body.data;
}

// --- PDFs -------------------------------------------------------------------

async function openBlobInTab(blob: Blob, targetWindow?: Window | null) {
  const url = URL.createObjectURL(blob);
  if (targetWindow && !targetWindow.closed) {
    targetWindow.location.href = url;
  } else {
    window.open(url, "_blank");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function openInvoicePdf(id: string, targetWindow?: Window | null): Promise<void> {
  const blob = await apiFetchBlob(`/billing/invoices/${id}/pdf/`);
  await openBlobInTab(blob, targetWindow);
}

export async function openStatementPdf(
  clientId: string,
  targetWindow?: Window | null,
): Promise<void> {
  const blob = await apiFetchBlob(`/billing/invoices/statement/?client=${clientId}`);
  await openBlobInTab(blob, targetWindow);
}
